// Как интерфейс карты называет базовую единицу активной страны.
//
// До появления второй страны слово «comune» было зашито в тексты локалей, и
// испанскому пользователю карта сообщала «Total: 8044 comuni». Термин — это
// свойство страны, а не продукта, поэтому он приходит из meta.admin_terms и
// подставляется в строки через {{unit}} / {{unitPlural}}.
//
// Русские варианты берутся ТОЛЬКО из meta (`place_ru`, `place_plural_ru`).
// Страна, не опубликовавшая их (Италия), получает значения по умолчанию из
// ADMIN_TERMS_FALLBACK — единственное место, где термин задан в коде.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { queries } from '@/data';
import { ADMIN_TERMS_FALLBACK } from '@/data/schemas';
import { activeMapCountryConfig } from '@/data/active-country';
import { uiLang } from '@/i18n/lang';

export interface UnitTerms {
  /** Единственное число: comune / municipality / коммуна / муниципалитет. */
  unit: string;
  /** Множественное: comuni / municipalities / коммуны / муниципалитеты. */
  unitPlural: string;
  /** Уровень над единицей: provincia / autonomous community / провинция. */
  admin1: string;
  /**
   * Название страны на языке интерфейса. Берётся из реестра карт
   * (MAP_COUNTRY_CONFIG), а не из meta.country_label: там лежит эндоним
   * («Italia»), а интерфейсу нужен экзоним на языке пользователя.
   */
  country: string;
  /**
   * Индексная сигнатура нужна react-i18next: t(key, options) требует
   * $Dictionary, а именованный интерфейс без неё туда не подставляется.
   */
  [extra: string]: string;
}

/**
 * Значения для интерполяции в строки локалей. Спред в t():
 *   t('filters.totalCount', { count, ...terms })
 */
export function useUnitTerms(): UnitTerms {
  const { i18n } = useTranslation();
  // enabled: false — хук ТОЛЬКО читает кеш и переподписывается на него.
  // Часть его потребителей (AiHelperBubble) живёт и вне карты: на /quiz/result
  // и /next-steps meta.json не нужен, и включённый запрос тянул бы файл страны
  // на страницах, которые карту не показывают. На маршруте карты meta уже
  // загружена (MapWithHeader держит её до рендера MapPage), поэтому кеш здесь
  // всегда полон, а вне карты подставляются термины по умолчанию.
  const { data: meta } = useQuery({ ...queries.meta(), enabled: false });
  const isRu = uiLang(i18n.language) === 'ru';
  const cfg = activeMapCountryConfig();

  return useMemo(() => {
    const terms = meta?.admin_terms ?? ADMIN_TERMS_FALLBACK;
    return {
      unit: isRu ? terms.place_ru : terms.place,
      unitPlural: isRu ? terms.place_plural_ru : terms.place_plural,
      admin1: isRu ? terms.admin1_ru : terms.admin1,
      country: isRu ? cfg.nameRu : cfg.nameEn,
    };
  }, [meta, isRu, cfg]);
}
